import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { DEFAULT_ORG_NAME } from './accountModel'
import { ACTIVE_CLASS, BlackMark, IDLE_CLASS, LINK_CLASS } from './FleetTopNav'
import { SignedInAvatar } from './SignedInAvatar'

export interface NavVariant {
  /** What this way of naming the tabs is called. */
  label: string
  /** The agent's own tab, which leads. */
  agent: string
  tabs: string[]
  /** The argument for and against this wording, where it has been made. */
  pros?: string[]
  cons?: string[]
}

/** The bar on its own, with nothing under it: this compares names, not screens. */
function Bar({ agent, tabs }: Omit<NavVariant, 'label'>) {
  return (
    <div className="border-ink-100 grid grid-cols-[1fr_auto_1fr] items-center gap-4 rounded-sm border bg-white px-4">
      <div className="flex min-w-0 items-center gap-3">
        <BlackMark />
        <span className="text-ink-900 truncate text-sm font-medium">{DEFAULT_ORG_NAME}</span>
        <ChevronDown className="text-ink-300 size-3 shrink-0" />
      </div>

      <div className="flex items-center gap-1">
        <span className={cn(LINK_CLASS, ACTIVE_CLASS)}>{agent}</span>
        <span aria-hidden className="bg-ink-100 mx-1 h-4 w-px" />
        {tabs.map((tab) => (
          <span key={tab} className={cn(LINK_CLASS, IDLE_CLASS)}>
            {tab}
          </span>
        ))}
      </div>

      <div className="flex justify-end">
        <SignedInAvatar />
      </div>
    </div>
  )
}

function Argument({ heading, points }: { heading: string; points?: string[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-ink text-xs font-semibold">{heading}</span>
      <ul className="text-ink-600 flex list-disc flex-col gap-1.5 pl-4 text-xs leading-relaxed">
        {points?.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
    </div>
  )
}

/** Ways of naming the same account, stacked so the wording can be compared. */
export function NavVariants({ variants }: { variants: NavVariant[] }) {
  return (
    <div className="flex flex-col gap-6">
      {variants.map((variant) => (
        <div key={variant.label} className="flex flex-col gap-2">
          <h3 className="text-ink text-lg font-bold tracking-tight">{variant.label}</h3>
          <Bar agent={variant.agent} tabs={variant.tabs} />
          {(variant.pros ?? variant.cons) && (
            <div className="grid grid-cols-2 gap-6 px-1 pt-1">
              <Argument heading="Pros" points={variant.pros} />
              <Argument heading="Cons" points={variant.cons} />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
